import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ApiService } from '../../services/api';
import { Tooltip } from '@mui/material';
import { renderCompactLink } from '../../utils/formatters';


interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any) => string;
  type?: 'boolean' | 'text' | 'number' | 'autocomplete' | 'industryTag' | 'country' | 'state';
  options?: string[]; // Options for autocomplete type
  nameToIdMap?: Map<string, number>; // Map for industry tag name to ID conversion
  idToNameMap?: Map<number, string>; // Map for industry tag ID to name conversion
}

interface DataTableProps {
  columns: Column[];
  rows: any[];
  page: number;
  rowsPerPage: number;
  totalRows: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSort?: (column: string) => void;
  sortConfig?: {
    column: string;
    direction: 'ASC' | 'DESC';
  };
  loading?: boolean;
  isEditMode?: boolean;
  onCellEdit?: (rowId: number, columnId: string, value: any) => void;
  editableColumns?: string[];
  savingRows?: Set<number>;
  isRowSelected?: (rowId: number) => boolean;
  selectedRowsCount?: number;
  selectAllVersion?: number;
  onRowSelect?: (rowId: number, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  stickyColumns?: string[]; // Array of column IDs that should be sticky
  hidePagination?: boolean; // Hide pagination controls (for keyset pagination views)
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  page,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  sortConfig,
  loading = false,
  isEditMode = false,
  onCellEdit,
  editableColumns,
  savingRows = new Set(),
  isRowSelected,
  selectedRowsCount = 0,
  selectAllVersion = 0,
  onRowSelect,
  onSelectAll,
  stickyColumns = [],
  hidePagination = false,
}) => {
  // State for industry tags mapping
  const [industryTagsMap, setIndustryTagsMap] = useState<Map<string, number>>(new Map());
  const [industryTagsIdMap, setIndustryTagsIdMap] = useState<Map<number, string>>(new Map());
  const [industryTagsOptions, setIndustryTagsOptions] = useState<string[]>([]);
  const [industryTagsLoading, setIndustryTagsLoading] = useState(false);

  // State for countries mapping
  const [countriesMap, setCountriesMap] = useState<Map<string, number>>(new Map());
  const [countriesIdMap, setCountriesIdMap] = useState<Map<number, string>>(new Map());
  const [countriesOptions, setCountriesOptions] = useState<string[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  // State for states mapping
  const [statesMap, setStatesMap] = useState<Map<string, number>>(new Map());
  const [statesIdMap, setStatesIdMap] = useState<Map<number, string>>(new Map());
  const [statesOptions, setStatesOptions] = useState<string[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);

  // Fetch industry tags when component mounts or when industry tag columns are present
  // Use a ref to track if we've already fetched to prevent duplicate calls
  const industryTagsFetchedRef = useRef(false);
  
  useEffect(() => {
    const hasIndustryTagColumn = columns.some(col => col.type === 'industryTag');
    if (hasIndustryTagColumn && !industryTagsFetchedRef.current && !industryTagsLoading) {
      industryTagsFetchedRef.current = true;
      setIndustryTagsLoading(true);
      
      // Fetch all industry tags in a single request (no limit on backend)
      const fetchAllIndustryTags = async () => {
        try {
          const response: any = await ApiService.getIndustryTags(null, undefined, 'name', 'ASC');
          
          if (response && response.data && Array.isArray(response.data)) {
            // Process all fetched tags
            const nameToId = new Map<string, number>();
            const idToName = new Map<number, string>();
            const options: string[] = [];
            
            response.data.forEach((tag: { id: number; name: string }) => {
              // Filter out undefined/invalid entries
              if (tag && tag.name && tag.name !== 'undefined') {
                nameToId.set(tag.name.toLowerCase().trim(), tag.id);
                idToName.set(tag.id, tag.name);
                options.push(tag.name);
              }
            });
            
            setIndustryTagsMap(nameToId);
            setIndustryTagsIdMap(idToName);
            setIndustryTagsOptions(options);
          }
          
          setIndustryTagsLoading(false);
        } catch (error) {
          console.error('Error fetching industry tags:', error);
          industryTagsFetchedRef.current = false; // Reset on error so it can retry
          setIndustryTagsLoading(false);
        }
      };
      
      fetchAllIndustryTags();
    }
  }, [columns, industryTagsLoading]);

  // Fetch countries when component mounts or when country columns are present
  // Use a ref to track if we've already fetched to prevent duplicate calls
  const countriesFetchedRef = useRef(false);
  
  useEffect(() => {
    const hasCountryColumn = columns.some(col => col.type === 'country');
    if (hasCountryColumn && !countriesFetchedRef.current && !countriesLoading) {
      countriesFetchedRef.current = true;
      setCountriesLoading(true);
      
      // Fetch all countries in a single request (no limit on backend)
      const fetchAllCountries = async () => {
        try {
          const response: any = await ApiService.getCountries(null, undefined, 'name', 'ASC');
          
          if (response && response.data && Array.isArray(response.data)) {
            // Process all fetched countries
            const nameToId = new Map<string, number>();
            const idToName = new Map<number, string>();
            const options: string[] = [];
            
            response.data.forEach((country: { id: number; name: string }) => {
              // Filter out undefined/invalid entries
              if (country && country.name && country.name !== 'undefined') {
                nameToId.set(country.name.toLowerCase().trim(), country.id);
                idToName.set(country.id, country.name);
                options.push(country.name);
              }
            });
            
            setCountriesMap(nameToId);
            setCountriesIdMap(idToName);
            setCountriesOptions(options);
          }
          
          setCountriesLoading(false);
        } catch (error) {
          console.error('Error fetching countries:', error);
          countriesFetchedRef.current = false; // Reset on error so it can retry
          setCountriesLoading(false);
        }
      };
      
      fetchAllCountries();
    }
  }, [columns, countriesLoading]);

  // Fetch states when component mounts or when state columns are present
  // Use a ref to track if we've already fetched to prevent duplicate calls
  const statesFetchedRef = useRef(false);
  
  useEffect(() => {
    const hasStateColumn = columns.some(col => col.type === 'state');
    if (hasStateColumn && !statesFetchedRef.current && !statesLoading) {
      statesFetchedRef.current = true;
      setStatesLoading(true);
      
      // Fetch all states in a single request (no limit on backend)
      const fetchAllStates = async () => {
        try {
          const response: any = await ApiService.getStates(null, undefined, 'name', 'ASC');
          
          if (response && response.data && Array.isArray(response.data)) {
            // Process all fetched states
            const nameToId = new Map<string, number>();
            const idToName = new Map<number, string>();
            const options: string[] = [];
            
            response.data.forEach((state: { id: number; name: string }) => {
              // Filter out undefined/invalid entries
              if (state && state.name && state.name !== 'undefined') {
                nameToId.set(state.name.toLowerCase().trim(), state.id);
                idToName.set(state.id, state.name);
                options.push(state.name);
              }
            });
            
            setStatesMap(nameToId);
            setStatesIdMap(idToName);
            setStatesOptions(options);
          }
          
          setStatesLoading(false);
        } catch (error) {
          console.error('Error fetching states:', error);
          statesFetchedRef.current = false; // Reset on error so it can retry
          setStatesLoading(false);
        }
      };
      
      fetchAllStates();
    }
  }, [columns, statesLoading]);

  // Function to truncate text to first N words
  const truncateToWords = (text: string, wordCount: number = 6) => {
    if (!text) return text;
    const words = text.split(' ');
    if (words.length <= wordCount) return text;
    return words.slice(0, wordCount).join(' ') + '...';
  };

  const isColumnEditable = useCallback((columnId: string) => {
    if (!isEditMode) return false;
    if (columnId === 'id') return false; // Never allow editing ID
    if (editableColumns) {
      return editableColumns.includes(columnId);
    }
    return true; // Default: all columns except ID are editable
  }, [isEditMode, editableColumns]);

  const handleCellChange = useCallback((rowId: number | undefined, columnId: string, newValue: any) => {
    if (onCellEdit && rowId !== undefined && rowId !== null) {
      onCellEdit(rowId, columnId, newValue);
    }
  }, [onCellEdit]);

  // Calculate left position for sticky columns
  const getStickyLeft = useCallback((columnId: string) => {
    if (!stickyColumns.includes(columnId)) return undefined;
    
    // Find the actual position of this column in the columns array
    const columnIndex = columns.findIndex(col => col.id === columnId);
    if (columnIndex === -1) return undefined;
    
    // In edit mode, start first sticky column at 47px (1px overlap with checkbox) to eliminate gap
    const isFirstSticky = columns.slice(0, columnIndex).every(col => !stickyColumns.includes(col.id));
    let left = isEditMode ? (isFirstSticky ? 47 : 48) : 0;
    
    // Calculate left position based on all previous columns (both sticky and non-sticky)
    for (let i = 0; i < columnIndex; i++) {
      const prevColumn = columns[i];
      if (prevColumn) {
        // Use actual width: 90px for regular columns in non-edit mode, minWidth for description
        // In edit mode, use a reasonable default width since columns aren't constrained
        if (prevColumn.id === 'description') {
          left += prevColumn.minWidth || 300;
        } else {
          // In edit mode, columns are wider, use a reasonable estimate
          // In non-edit mode, use fixed 90px
          left += isEditMode ? 150 : 90;
        }
      }
    }
    return left;
  }, [stickyColumns, columns, isEditMode]);

  // Check if column is sticky
  const isSticky = useCallback((columnId: string) => {
    return stickyColumns.includes(columnId);
  }, [stickyColumns]);

  // Editable cell component with local state for better performance
  // Local state prevents parent re-renders on every keystroke
  const EditableCell = React.memo<{
    value: any;
    rowId: number;
    columnId: string;
    column: Column;
    isSaving: boolean;
    onBlur: (value: any) => void;
  }>(({ value, rowId, columnId, column, isSaving, onBlur }) => {
    // For lookup columns (industryTag, country, state), convert ID to name for display
    const getDisplayValue = (val: any, col: Column): string => {
      if (val !== null && val !== undefined) {
        const id = typeof val === 'number' ? val : Number(val);
        if (!isNaN(id)) {
          if (col.type === 'industryTag' && industryTagsIdMap.has(id)) {
            return industryTagsIdMap.get(id) || '';
          }
          if (col.type === 'country' && countriesIdMap.has(id)) {
            return countriesIdMap.get(id) || '';
          }
          if (col.type === 'state' && statesIdMap.has(id)) {
            return statesIdMap.get(id) || '';
          }
        }
        // If no match found for lookup type, return empty
        if (col.type === 'industryTag' || col.type === 'country' || col.type === 'state') {
          return '';
        }
      }
      return val === null || val === undefined ? '' : String(val);
    };

    // Handle 0 correctly - 0 is a valid value, not falsy
    const initialValue = getDisplayValue(value, column);
    const [localValue, setLocalValue] = useState(initialValue);
    const [isFocused, setIsFocused] = useState(false);
    const previousValueRef = useRef(value);
    const justBlurredRef = useRef(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    const hasSelectedOnFocusRef = useRef(false);
    const isAutocomplete = column.type === 'autocomplete' && column.options;
    const isIndustryTag = column.type === 'industryTag';
    const isCountry = column.type === 'country';
    const isState = column.type === 'state';
    const isLookupType = isIndustryTag || isCountry || isState;

    // Custom lightweight dropdown state
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Get options and loading state based on type - memoized to prevent dependency issues
    // Note: industryTagsOptions, countriesOptions, statesOptions are state variables that DO trigger re-renders
    const allOptions = useMemo(() => {
      if (isIndustryTag) return industryTagsOptions;
      if (isCountry) return countriesOptions;
      if (isState) return statesOptions;
      return column.options || [];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIndustryTag, isCountry, isState, column.options, industryTagsOptions, countriesOptions, statesOptions]);

    const isLoading = isIndustryTag 
      ? industryTagsLoading 
      : isCountry 
        ? countriesLoading 
        : isState 
          ? statesLoading 
          : false;
    
    // Get the appropriate lookup map for converting names to IDs
    const lookupMap = isIndustryTag 
      ? industryTagsMap 
      : isCountry 
        ? countriesMap 
        : isState 
          ? statesMap 
          : new Map<string, number>();

    // Update local value when prop value changes, but only if:
    // 1. Not currently focused (user isn't editing)
    // 2. Not just blurred (give time for blur to complete)
    // 3. Value actually changed from what we last saw
    // This prevents resetting during active editing or right after blur
    useEffect(() => {
      // If we just blurred, clear the flag after a short delay
      if (justBlurredRef.current) {
        const timer = setTimeout(() => {
          justBlurredRef.current = false;
        }, 100);
        return () => clearTimeout(timer);
      }

      // Only sync if not focused, not just blurred, and value actually changed
      // Use strict equality to handle 0 correctly (0 !== null, 0 !== undefined)
      if (!isFocused && !justBlurredRef.current && value !== previousValueRef.current) {
        // For industry tag, convert ID to name for display
        const newValue = getDisplayValue(value, column);
        setLocalValue(newValue);
        previousValueRef.current = value;
      } else if (!isFocused && !justBlurredRef.current) {
        // Update ref even if we don't sync (to track external changes)
        previousValueRef.current = value;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isFocused, column]);

    // Filter options based on input value
    useEffect(() => {
      if (isAutocomplete || isLookupType) {
        if (localValue) {
          const filtered = allOptions.filter(opt => 
            opt.toLowerCase().includes(localValue.toLowerCase())
          );
          setFilteredOptions(filtered);
        } else {
          setFilteredOptions(allOptions);
        }
      }
    }, [localValue, allOptions, isAutocomplete, isLookupType]);

    // Close dropdown on outside scroll or click
    useEffect(() => {
      if ((isAutocomplete || isLookupType) && dropdownOpen) {
        const handleScroll = (e: Event) => {
          const target = e.target as HTMLElement;
          // Check if scroll is within the dropdown
          if (dropdownRef.current && dropdownRef.current.contains(target)) {
            return; // Don't close if scrolling inside dropdown
          }
          // Close if scrolling outside
          setDropdownOpen(false);
          if (inputRef.current) {
            inputRef.current.blur();
          }
        };

        const handleClickOutside = (e: MouseEvent) => {
          if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
            setDropdownOpen(false);
          }
        };

        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('wheel', handleScroll, true);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
          window.removeEventListener('scroll', handleScroll, true);
          window.removeEventListener('wheel', handleScroll, true);
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
    }, [dropdownOpen, isAutocomplete, isLookupType]);

    // Simple click handler - just stop propagation to prevent TableCell onClick
    const handleMouseDown = (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Stop propagation to prevent TableCell onClick from interfering
      e.stopPropagation();
      // Let browser handle all click behavior naturally (cursor placement, selection, etc.)
    };

    // OPTIMIZATION: Use native HTML input instead of Material-UI TextField
    // This is much lighter and faster, especially with 50+ rows × 35 columns
    const isMultiline = column.id === 'observations' || column.id === 'description' || column.id === 'note';
    const inputStyle: React.CSSProperties = {
      width: '100%',
      minWidth: column.type === 'number' ? 100 : 120,
      padding: '6px 8px', // Reduced padding for better fit
      border: '1px solid #ccc',
      borderRadius: '4px',
      fontSize: '14px',
      fontFamily: 'inherit',
      backgroundColor: isSaving ? '#f5f5f5' : '#fff',
      cursor: isSaving ? 'not-allowed' : 'text',
      resize: isMultiline ? 'vertical' : 'none',
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsFocused(true);
      // Only select all on the very first focus (when user first clicks on the cell)
      if (!hasSelectedOnFocusRef.current && e.target.value.length > 0) {
        setTimeout(() => {
          if (e.target) {
            e.target.select();
          }
        }, 0);
        hasSelectedOnFocusRef.current = true;
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsFocused(false);
      justBlurredRef.current = true;
      hasSelectedOnFocusRef.current = false;
      
      if (column.type === 'number') {
        const trimmedValue = e.target.value.trim();
        let numValue: number | null = null;
        if (trimmedValue !== '') {
          const parsed = Number(trimmedValue);
          if (!isNaN(parsed)) {
            numValue = parsed;
          }
        }
        onBlur(numValue);
      } else {
        // Convert empty strings to null for database compatibility
        const trimmedValue = e.target.value.trim();
        onBlur(trimmedValue === '' ? null : trimmedValue);
      }
    };

    // Handle lookup types (industryTag, country, state) - show names but save IDs
    if (isLookupType) {
      const handleLookupFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        handleFocus(e);
        setDropdownOpen(true);
      };

      const handleLookupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
        setDropdownOpen(true);
      };

      const handleLookupBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const name = e.target.value.trim();
        
        // If field is empty, handle immediately - no need to delay (same as regular columns)
        if (!name) {
          setIsFocused(false);
          setDropdownOpen(false);
          justBlurredRef.current = true;
          hasSelectedOnFocusRef.current = false;
          onBlur(null);
          return;
        }
        
        // Field has a value - delay blur to allow option click from dropdown
        setTimeout(() => {
          setIsFocused(false);
          setDropdownOpen(false);
          justBlurredRef.current = true;
          hasSelectedOnFocusRef.current = false;
          
          // Convert name to ID using the appropriate lookup map
          // Normalize the name (lowercase and trim) to match how it's stored in the map
          let idToSave: number | null = null;
          const normalizedName = name.toLowerCase().trim();
          if (lookupMap.has(normalizedName)) {
            idToSave = lookupMap.get(normalizedName)!;
          } else {
            // Try to parse as ID if name not found
            const parsedId = Number(name);
            if (!isNaN(parsedId)) {
              idToSave = parsedId;
            }
          }
          onBlur(idToSave);
        }, 200);
      };

      const handleOptionClick = (option: string) => {
        setLocalValue(option);
        setDropdownOpen(false);
        if (inputRef.current) {
          inputRef.current.blur();
        }
      };

      const lookupTypeKey = isIndustryTag ? 'industry-tag' : isCountry ? 'country' : 'state';

      return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
          <input
            type="text"
            value={localValue}
            ref={inputRef as React.RefObject<HTMLInputElement>}
            onFocus={handleLookupFocus}
            onMouseDown={handleMouseDown}
            onBlur={handleLookupBlur}
            onChange={handleLookupChange}
            disabled={isSaving || isLoading}
            style={inputStyle}
            placeholder={isLoading ? 'Loading...' : ''}
          />
          {dropdownOpen && filteredOptions.length > 0 && (
            <div
              ref={dropdownRef}
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                width: 'max-content',
                minWidth: '300px',
                maxWidth: '500px',
                zIndex: 1000,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                maxHeight: '200px',
                overflowY: 'auto',
                marginTop: '2px',
              }}
              onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
            >
              {filteredOptions.map((option, index) => (
                <div
                  key={`${lookupTypeKey}-${option}-${index}`}
                  onClick={() => handleOptionClick(option)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    textOverflow: 'clip',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Handle autocomplete type
    if (isAutocomplete) {
      const handleAutocompleteFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        handleFocus(e);
        setDropdownOpen(true);
      };

      const handleAutocompleteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
        setDropdownOpen(true);
      };

      const handleAutocompleteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const trimmedValue = e.target.value.trim();
        
        // If field is empty, handle immediately - no need to delay (same as regular columns)
        if (trimmedValue === '') {
          setIsFocused(false);
          setDropdownOpen(false);
          justBlurredRef.current = true;
          hasSelectedOnFocusRef.current = false;
          onBlur(null);
          return;
        }
        
        // Field has a value - delay blur to allow option click from dropdown
        setTimeout(() => {
          setIsFocused(false);
          setDropdownOpen(false);
          justBlurredRef.current = true;
          hasSelectedOnFocusRef.current = false;
          onBlur(trimmedValue);
        }, 200);
      };

      const handleOptionClick = (option: string) => {
        setLocalValue(option);
        setDropdownOpen(false);
        if (inputRef.current) {
          inputRef.current.blur();
        }
      };

      return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
          <input
            type="text"
            value={localValue}
            ref={inputRef as React.RefObject<HTMLInputElement>}
            onFocus={handleAutocompleteFocus}
            onMouseDown={handleMouseDown}
            onBlur={handleAutocompleteBlur}
            onChange={handleAutocompleteChange}
            disabled={isSaving}
            style={inputStyle}
          />
          {dropdownOpen && filteredOptions.length > 0 && (
            <div
              ref={dropdownRef}
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                width: 'max-content',
                minWidth: '300px',
                maxWidth: '500px',
                zIndex: 1000,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                maxHeight: '200px',
                overflowY: 'auto',
                marginTop: '2px',
              }}
              onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
            >
              {filteredOptions.map((option, index) => (
                <div
                  key={`autocomplete-${option}-${index}`}
                  onClick={() => handleOptionClick(option)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    textOverflow: 'clip',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (isMultiline) {
      return (
        <textarea
          value={localValue}
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          onFocus={handleFocus}
          onMouseDown={handleMouseDown}
          onBlur={handleBlur}
          onChange={(e) => setLocalValue(e.target.value)}
          disabled={isSaving}
          style={inputStyle}
          rows={2}
        />
      );
    }

    return (
      <input
        type={column.type === 'number' ? 'text' : 'text'}
        value={localValue}
        ref={inputRef as React.RefObject<HTMLInputElement>}
        onFocus={handleFocus}
        onMouseDown={handleMouseDown}
        onBlur={handleBlur}
        onChange={(e) => setLocalValue(e.target.value)}
        disabled={isSaving}
        style={inputStyle}
      />
    );
  }, (prevProps, nextProps) => {
    // Only re-render if value, saving state, rowId, or columnId changed
    // This prevents re-renders when other cells in the same row change
    return (
      prevProps.value === nextProps.value &&
      prevProps.isSaving === nextProps.isSaving &&
      prevProps.rowId === nextProps.rowId &&
      prevProps.columnId === nextProps.columnId
    );
  });

  // Isolated checkbox component with local state - completely independent
  // Updates immediately, never syncs with parent, parent never notified during selection
  const SelectionCheckbox = React.memo<{
    rowId: number;
    isSelected: boolean;
    onRowSelect?: (rowId: number, selected: boolean) => void;
  }>(({ rowId, isSelected, onRowSelect }) => {
    const [localChecked, setLocalChecked] = useState(isSelected);

    // Only sync on mount or when prop changes externally (like select-all)
    useEffect(() => {
      setLocalChecked(isSelected);
    }, [isSelected]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      const newChecked = e.target.checked;
      // Update local state immediately (instant UI feedback)
      setLocalChecked(newChecked);
      // Update ref only - NO parent state update, NO re-render
      if (onRowSelect) {
        onRowSelect(rowId, newChecked);
      }
    };

    return (
      <input
        type="checkbox"
        checked={localChecked}
        onChange={handleChange}
        style={{
          width: '18px',
          height: '18px',
          cursor: 'pointer',
          margin: 0
        }}
      />
    );
  }, (prevProps, nextProps) => {
    // Only re-render if rowId changed (different checkbox)
    // Selection state is managed locally, so we don't need to compare isSelected
    return prevProps.rowId === nextProps.rowId;
  });

  // Memoized row component to prevent re-renders when other rows' selection changes
  const TableRowWithCheckbox = React.memo<{
    row: any;
    rowId: number | undefined;
    index: number;
    columns: Column[];
    isEditMode: boolean;
    isColumnEditable: (columnId: string) => boolean;
    isSaving: boolean;
    isSelected: boolean;
    selectAllVersion: number;
    onRowSelect?: (rowId: number, selected: boolean) => void;
    handleCellChange: (rowId: number | undefined, columnId: string, newValue: any) => void;
    stickyColumns: string[];
    getStickyLeft: (columnId: string) => number | undefined;
    isSticky: (columnId: string) => boolean;
  }>(({ row, rowId, index, columns, isEditMode, isColumnEditable, isSaving, isSelected, selectAllVersion, onRowSelect, handleCellChange, stickyColumns, getStickyLeft, isSticky }) => {
    return (
      <tr role="checkbox" aria-checked={isSelected} tabIndex={-1} style={{ 
        backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
        cursor: 'default'
      }}>
        {/* Row selection checkbox in edit mode */}
        {isEditMode && rowId !== undefined && rowId !== null && (
          <td 
            data-checkbox-column="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'sticky',
              left: 0,
              zIndex: 14,
              backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
              padding: '4px',
              borderBottom: '1px solid #e0e0e0',
              textAlign: 'center',
            }}
          >
            <SelectionCheckbox
              rowId={rowId}
              isSelected={isSelected}
              onRowSelect={onRowSelect}
            />
          </td>
        )}
            {columns.map((column) => {
          const value = row[column.id];
          const isEditable = isColumnEditable(column.id);
          const isSavingCell = isSaving;
          const stickyLeft = getStickyLeft(column.id);
          const isColumnSticky = isSticky(column.id);
          const columnWidth = !isEditMode 
            ? (column.minWidth ? `${column.minWidth}px` : '90px') 
            : 'auto';
          
          return (
            <td 
              key={column.id} 
              align={column.align || 'left'}
              data-sticky={isColumnSticky ? 'true' : undefined}
              style={{
                padding: isEditMode ? '4px 8px' : '6px 4px',
                borderBottom: '1px solid #e0e0e0',
                borderLeft: 'none',
                borderRight: 'none',
                borderTop: 'none',
                minWidth: columnWidth,
                maxWidth: columnWidth,
                width: columnWidth,
                ...(isColumnSticky && stickyLeft !== undefined ? {
                  position: 'sticky',
                  left: `${stickyLeft}px`,
                  zIndex: 15,
                  backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
                } : {}),
              }}
              onClick={(e) => {
                // If clicking on the cell (not the input), just focus the input
                // The onFocus handler will handle selecting all on first focus
                if (isEditable && rowId !== undefined && rowId !== null) {
                  const input = e.currentTarget.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
                  if (input && e.target !== input && !input.contains(e.target as Node)) {
                    e.stopPropagation();
                    // Just focus - let the onFocus handler select all on first focus
                    setTimeout(() => {
                      input.focus();
                    }, 0);
                  }
                }
              }}
            >
              {isEditable ? (
                // Editable cell with local state for performance
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <EditableCell
                    value={value}
                    rowId={rowId!}
                    columnId={column.id}
                    column={column}
                    isSaving={isSavingCell}
                    onBlur={(newValue) => handleCellChange(rowId, column.id, newValue)}
                  />
                  {isSavingCell && (
                    <div 
                      style={{ 
                        position: 'absolute', 
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '16px',
                        height: '16px',
                        border: '2px solid #e0e0e0',
                        borderTopColor: '#1976d2',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }} 
                    />
                  )}
                </div>
            ) : (
              // Read-only cell
              (column.id === 'description' && value) ? (
                // Special handling for description: truncate to 5 words with tooltip
                <span title={String(value)} style={{ cursor: 'help', fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                  {truncateToWords(String(value), 5)}
                </span>
              ) : (column.id === 'location' && value) ? (
                // Special handling for location: truncate to 5 words with tooltip (same as description)
                <span title={String(value)} style={{ cursor: 'help', fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                  {truncateToWords(String(value), 5)}
                </span>
              ) : (column.id === 'brand_follower' && value) ? (
                // Special handling for brand_follower: truncate to 5 words with tooltip (same as description)
                <span title={String(value)} style={{ cursor: 'help', fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                  {truncateToWords(String(value), 5)}
                </span>
              ) : (column.id === 'new_values' && value) ? (
                // Special handling for new_values: truncate to 10 words with tooltip showing full formatted value
                <span 
                  title={column.format ? column.format(value) : String(value)} 
                  style={{ cursor: 'help', fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                >
                  {truncateToWords(column.format ? column.format(value) : String(value), 10)}
                </span>
              ) : (['company_address', 'address', 'note', 'notes', 'license', 'licence'].includes(column.id.toLowerCase()) && value) ? (
                // Special handling for address, notes, license: truncate to 5 words with Material-UI Tooltip rollover
                <Tooltip title={String(value)} arrow>
                  <span style={{ cursor: 'help', fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                    {truncateToWords(String(value), 5)}
                  </span>
                </Tooltip>
              ) : (['website', 'linkedin', 'linkedin_id', 'facebook', 'youtube', 'ig', 'twitter'].includes(column.id.toLowerCase()) && value) ? (
                renderCompactLink(value, column.id)
              ) : (
                // Regular cells: allow wrapping for 15 character width
                <span 
                  style={{ 
                    fontSize: '0.875rem',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: 1.4,
                  }}
                >
                  {column.id === 'id' && (value === null || value === undefined || (typeof value === 'number' && value < 0)) 
                    ? '' // Hide negative IDs (new rows)
                    : (value !== null && value !== undefined 
                        ? (column.format ? column.format(value) : String(value))
                        : '')
                  }
                </span>
              )
            )}
            </td>
          );
        })}
      </tr>
    );
  }, (prevProps: any, nextProps: any) => {
    // Only re-render if selection state, saving state, row ID, edit mode, select-all version, or row data changed
    // Select-all version forces re-render when select-all is clicked
    // Shallow compare row data - only check if row reference changed (React will handle this)
    // For new rows, rowId will be different, so they'll re-render appropriately
    return (
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isSaving === nextProps.isSaving &&
      prevProps.rowId === nextProps.rowId &&
      prevProps.isEditMode === nextProps.isEditMode &&
      prevProps.selectAllVersion === nextProps.selectAllVersion &&
      prevProps.row === nextProps.row && // Reference equality - React will handle this
      prevProps.index === nextProps.index &&
      JSON.stringify(prevProps.stickyColumns) === JSON.stringify(nextProps.stickyColumns)
    );
  });

  // Refs for scroll synchronization
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const topScrollbarRef = useRef<HTMLDivElement>(null);
  const [showTopScrollbar, setShowTopScrollbar] = useState(false);
  const [tableScrollWidth, setTableScrollWidth] = useState(1200);

  // Check if horizontal scroll is needed and get table width
  useEffect(() => {
    const checkScroll = () => {
      if (tableContainerRef.current) {
        const scrollWidth = tableContainerRef.current.scrollWidth;
        const clientWidth = tableContainerRef.current.clientWidth;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        setShowTopScrollbar(hasHorizontalScroll);
        if (hasHorizontalScroll) {
          setTableScrollWidth(scrollWidth);
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      checkScroll();
      // Also check after a short delay to ensure table is fully rendered
      setTimeout(checkScroll, 100);
    });

    // Check on window resize
    window.addEventListener('resize', checkScroll);
    // Check when rows or columns change
    const observer = new MutationObserver(checkScroll);
    if (tableContainerRef.current) {
      observer.observe(tableContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', checkScroll);
      observer.disconnect();
    };
  }, [rows, columns]);

  // Sync scroll between top scrollbar and main container
  const handleTopScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      tableContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  const handleMainScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (topScrollbarRef.current && tableContainerRef.current) {
      topScrollbarRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: translateY(-50%) rotate(360deg); }
        }
      `}</style>
      <div style={{ width: '100%', overflow: 'visible', boxShadow: 'none', position: 'relative' }}>
      <div 
        ref={tableContainerRef}
        onScroll={handleMainScroll}
        style={{ 
          overflow: 'auto',
          maxHeight: 'none',
          position: 'relative',
          scrollbarWidth: 'thin',
          scrollbarColor: '#888 #f1f1f1',
          ...(showTopScrollbar ? {
            marginBottom: '-17px',
            paddingBottom: '17px',
          } : {}),
        }}
      >
        <table 
          aria-label="data table" 
          style={{ 
            width: 'max-content',
            borderCollapse: 'separate',
            borderSpacing: 0,
          }}
        >
          <thead>
            <tr>
              {/* Select All checkbox in edit mode */}
              {isEditMode && (
                <th 
                  data-checkbox-column="true"
                  style={{ 
                    width: 48,
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    backgroundColor: '#ffffff',
                    zIndex: 17,
                    borderBottom: '2px solid #e0e0e0',
                    padding: isEditMode ? '16px' : '10px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    cursor: 'default',
                    userSelect: 'none',
                    height: isEditMode ? '56px' : '48px',
                  }} 
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    ref={(el) => {
                      if (el) {
                        const isIndeterminate = !!(selectedRowsCount > 0 && isRowSelected && rows.some(row => row.id && !isRowSelected(row.id)));
                        el.indeterminate = isIndeterminate;
                      }
                    }}
                    checked={
                      rows.length > 0 &&
                      isRowSelected &&
                      rows.every(row => row.id && isRowSelected(row.id))
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      if (onSelectAll) {
                        onSelectAll(e.target.checked);
                      }
                    }}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      margin: 0
                    }}
                  />
                </th>
              )}
               {columns.map((column) => {
                 const stickyLeft = getStickyLeft(column.id);
                 const isColumnSticky = isSticky(column.id);
                 const columnWidth = !isEditMode 
                    ? (column.minWidth ? `${column.minWidth}px` : '90px') 
                    : 'auto';
                 return (
                 <th
                   key={column.id}
                   align={column.align || 'left'}
                   data-sticky={isColumnSticky ? 'true' : undefined}
                   style={{
                     position: 'sticky',
                     top: 0,
                     backgroundColor: '#ffffff',
                     zIndex: isColumnSticky ? 16 : 10,
                     borderBottom: '2px solid #e0e0e0',
                     fontWeight: 'bold',
                     fontSize: '0.75rem', // Reduced font size
                     cursor: onSort ? 'pointer' : 'default',
                     userSelect: 'none',
                     padding: isEditMode ? '16px' : '8px 4px',
                     height: isEditMode ? '56px' : '36px',
                     minWidth: columnWidth,
                     maxWidth: columnWidth,
                     width: columnWidth,
                     ...(isColumnSticky && stickyLeft !== undefined ? {
                       left: `${stickyLeft}px`,
                     } : {}),
                     borderLeft: 'none',
                     borderRight: 'none',
                     borderTop: 'none',
                     transition: onSort ? 'background-color 0.2s' : 'none',
                   }}
                   onClick={() => {
                     if (onSort) {
                       onSort(column.id);
                     }
                   }}
                   onMouseEnter={(e) => {
                     if (onSort) {
                       e.currentTarget.style.backgroundColor = '#f5f5f5';
                     }
                   }}
                   onMouseLeave={(e) => {
                     if (onSort) {
                       e.currentTarget.style.backgroundColor = '#ffffff';
                     }
                   }}
                   title={onSort ? 'Click to sort' : undefined}
                 >
                   <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start' }}>
                      {['deliverable', 'risky', 'undeliverable', 'bounce'].includes(column.id) ? (
                        <Tooltip title={
                          column.id === 'deliverable' ? 'Deliverable' :
                          column.id === 'risky' ? 'Risky' :
                          column.id === 'undeliverable' ? 'Undeliverable' :
                          'Bounce'
                        } arrow>
                          <span style={{ borderBottom: '1px dotted #ccc', cursor: 'help' }}>{column.label}</span>
                        </Tooltip>
                      ) : (
                        <span>{column.label}</span>
                      )}
                      {sortConfig && sortConfig.column === column.id ? (
                        <span style={{ fontSize: '14px', color: '#1976d2', fontWeight: 'bold' }}>
                          {sortConfig.direction === 'ASC' ? '↑' : '↓'}
                        </span>
                      ) : onSort ? (
                        <span style={{ fontSize: '12px', color: '#999', opacity: 0.5 }}>⇅</span>
                      ) : null}
                    </div>
                 </th>
               );
               })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (isEditMode ? 1 : 0)} align="center" style={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
                  <span>Loading...</span>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (isEditMode ? 1 : 0)} align="center" style={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
                  <span>No data available</span>
                </td>
              </tr>
            ) : (
              (() => {
                // Server-side pagination: rows already contains only current page's data
                return rows.map((row, index) => {
                  const rowId = row.id;
                  const isSaving = rowId !== undefined && rowId !== null && savingRows.has(rowId);
                  const rowIsSelected = rowId !== undefined && rowId !== null && isRowSelected ? isRowSelected(rowId) : false;
                  
                  // Use memoized row component - only re-renders when this specific row's props change
                  // Use stable key: rowId for existing rows, negative rowId for new rows, or index as fallback
                  const stableKey = rowId !== undefined && rowId !== null ? `row-${rowId}` : `row-new-${index}`;
                  return (
                    <TableRowWithCheckbox
                      key={stableKey}
                      row={row}
                      rowId={rowId}
                      index={index}
                      columns={columns}
                      isEditMode={isEditMode}
                      isColumnEditable={isColumnEditable}
                      isSaving={isSaving}
                      isSelected={rowIsSelected}
                      selectAllVersion={selectAllVersion}
                      onRowSelect={onRowSelect}
                      handleCellChange={handleCellChange}
                      stickyColumns={stickyColumns}
                      getStickyLeft={getStickyLeft}
                      isSticky={isSticky}
                    />
                        );
                });
              })()
            )}
          </tbody>
        </table>
      </div>
      {/* Bottom horizontal scrollbar - sticky at bottom, always visible when needed */}
      {showTopScrollbar && (
        <div
          ref={topScrollbarRef}
          onScroll={handleTopScroll}
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 25,
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            height: '15px',
            backgroundColor: '#ffffff',
            borderTop: '1px solid #e0e0e0',
            boxShadow: '0 -1px 3px rgba(0,0,0,0.1)',
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 #f1f1f1',
          }}
        >
          <div
            style={{
              height: '1px',
              width: `${tableScrollWidth}px`,
            }}
          />
        </div>
      )}
      {/* Pagination Controls */}
      {!hidePagination && (
        <div style={{
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '8px 16px',
          gap: '16px'
        }}>
          <span style={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
            {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, totalRows)} of {totalRows}
          </span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={(e) => onPageChange(e, page - 1)}
              disabled={page === 0}
              style={{
                padding: '4px 8px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                backgroundColor: page === 0 ? 'rgba(0, 0, 0, 0.04)' : '#fff',
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                opacity: page === 0 ? 0.5 : 1
              }}
            >
              ‹
            </button>
            <button
              onClick={(e) => onPageChange(e, page + 1)}
              disabled={page >= Math.ceil(totalRows / rowsPerPage) - 1}
              style={{
                padding: '4px 8px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                backgroundColor: page >= Math.ceil(totalRows / rowsPerPage) - 1 ? 'rgba(0, 0, 0, 0.04)' : '#fff',
                cursor: page >= Math.ceil(totalRows / rowsPerPage) - 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                opacity: page >= Math.ceil(totalRows / rowsPerPage) - 1 ? 0.5 : 1
              }}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default DataTable;
